import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Permission {
  slug: string;
  display_name: string;
  module: string;
  description?: string;
}

export interface RoleWithPermissions {
  value: string;
  display_name: string;
  description?: string;
  permissions: string[];
}

export interface OverrideItem {
  permission_slug: string;
  is_granted: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.appUrl}${environment.apis.base}/auth-permissions`;
  
  // Cache user permissions in localStorage to avoid flashing UI on route activation
  private permissionsSubject = new BehaviorSubject<string[]>(
    JSON.parse(localStorage.getItem('user_permissions') || '[]')
  );
  
  public userPermissions$ = this.permissionsSubject.asObservable();

  // Indica si los permisos efectivos han sido cargados (del backend o de un caché válido)
  private permissionsLoadedSubject = new BehaviorSubject<boolean>(
    localStorage.getItem('user_permissions') !== null
  );
  public permissionsLoaded$ = this.permissionsLoadedSubject.asObservable();

  /**
   * Obtiene y cachea los permisos efectivos del usuario actual desde el backend
   */
  loadPermissions(userUpn?: string): Observable<any> {
    if (userUpn) {
      const cachedUpn = localStorage.getItem('user_permissions_upn');
      if (cachedUpn !== userUpn) {
        console.log('[PermissionService] Cambió el UPN o no coincide con el caché. Limpiando permisos anteriores.');
        this.clearPermissions();
      }
      localStorage.setItem('user_permissions_upn', userUpn);
    }

    return this.http.get<{ permissions: string[] }>(`${this.apiUrl}/my-permissions`).pipe(
      tap(res => {
        const perms = res.permissions || [];
        localStorage.setItem('user_permissions', JSON.stringify(perms));
        this.permissionsSubject.next(perms);
        this.permissionsLoadedSubject.next(true);
        console.log('[PermissionService] Permisos dinámicos cargados:', perms);
      }),
      catchError(err => {
        console.error('[PermissionService] Error cargando permisos dinámicos:', err);
        // Fallback a los permisos cacheados si la API falla temporalmente
        this.permissionsLoadedSubject.next(true); // Desbloqueamos de todos modos para que no quede pegado el guard
        return of({ permissions: this.permissionsSubject.value });
      })
    );
  }

  /**
   * Verifica de forma síncrona si el usuario tiene un permiso específico.
   * Soporta bypass de administrador del módulo ({modulo}.admin).
   */
  hasPermission(permission: string): boolean {
    const currentPerms = this.permissionsSubject.value;
    
    // 1. Si tiene el permiso exacto asignado
    if (currentPerms.includes(permission)) return true;
    
    // 2. Soporte para bypass granular de administrador del módulo (modulo.admin)
    if (permission.includes('.')) {
      const [module] = permission.split('.');
      const adminPerm = `${module}.admin`;
      if (currentPerms.includes(adminPerm)) return true;
    }
    
    return false;
  }

  /**
   * Verifica si el usuario tiene al menos uno de los permisos del listado
   */
  hasAnyPermission(permissions: string[]): boolean {
    if (!permissions || permissions.length === 0) return true;
    return permissions.some(p => this.hasPermission(p));
  }

  /**
   * Limpia los permisos almacenados (útil en el logout)
   */
  clearPermissions(): void {
    localStorage.removeItem('user_permissions');
    localStorage.removeItem('user_permissions_upn');
    sessionStorage.removeItem('login_logged');
    this.permissionsSubject.next([]);
    this.permissionsLoadedSubject.next(false);
  }

  /**
   * Registra el inicio de sesión del usuario en el backend
   */
  logUserLogin(): Observable<any> {
    return this.http.post(`${this.apiUrl}/log-login`, {});
  }

  /**
   * Obtiene el historial de actividades del usuario autenticado actual
   */
  getMyActivity(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/my-activity`);
  }

  // --- APIs Administrativas ---

  /**
   * Lista todos los permisos lógicos del catálogo del sistema
   */
  getPermissionsList(): Observable<Permission[]> {
    return this.http.get<Permission[]>(`${this.apiUrl}/permissions`);
  }

  /**
   * Obtiene la matriz completa de roles y sus permisos asignados
   */
  getRolesPermissions(): Observable<RoleWithPermissions[]> {
    return this.http.get<RoleWithPermissions[]>(`${this.apiUrl}/roles-permissions`);
  }

  /**
   * Actualiza la asignación de permisos para un rol específico
   */
  updateRolePermissions(roleValue: string, permissions: string[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/roles/${roleValue}/permissions`, { permissions });
  }

  /**
   * Obtiene los overrides específicos asignados a un usuario individual (UUID)
   */
  getUserOverrides(userId: string): Observable<OverrideItem[]> {
    return this.http.get<OverrideItem[]>(`${this.apiUrl}/users/${userId}/overrides`);
  }

  /**
   * Actualiza las excepciones de permisos (overrides) para un usuario individual (UUID)
   */
  updateUserOverrides(userId: string, overrides: OverrideItem[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/users/${userId}/overrides`, { overrides });
  }

  /**
   * Actualiza los metadatos (nombre y descripción) de un permiso lógico del catálogo
   */
  updatePermission(slug: string, displayName: string, description: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/permissions/${slug}`, {
      display_name: displayName,
      description
    });
  }
}
